import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, HourglassIcon, Filter, Loader2, RefreshCcw, Pencil, X, Trash2, Download, FileText } from 'lucide-react';
import { API, useAdminAuth } from '../context/AdminAuthContext';
import AdminLayout from '../components/AdminLayout';
import OvertimeSlaChip from '../components/OvertimeSlaChip';

/**
 * Feb 2026 — AdminWorkTracking
 *
 * Combined super-admin surface: approve/reject overtime requests
 * AND browse a filterable audit trail of overtime + desk activity
 * across every admin. Filters: date range + admin selector.
 */

const StatusChip = ({ status }) => {
  const map = {
    pending:  { bg: '#FEF3C7', ink: '#92400E', Icon: HourglassIcon },
    approved: { bg: '#D1FAE5', ink: '#065F46', Icon: CheckCircle2 },
    rejected: { bg: '#FEE2E2', ink: '#991B1B', Icon: XCircle },
  };
  const s = map[status] || map.pending;
  const Icon = s.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: s.bg, color: s.ink }}
    >
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const muscatToday = () => {
  const d = new Date();
  return new Date(d.getTime() + 4 * 60 * 60 * 1000).toISOString().slice(0, 10);
};
const muscatMonthStart = () => {
  const d = new Date();
  const shifted = new Date(d.getTime() + 4 * 60 * 60 * 1000);
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-01`;
};

const AdminWorkTracking = () => {
  const { authHeaders, isSuperAdmin } = useAdminAuth();
  const [dateFrom, setDateFrom] = useState(muscatMonthStart());
  const [dateTo, setDateTo] = useState(muscatToday());
  const [adminId, setAdminId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [reviewNote, setReviewNote] = useState({});
  const [tab, setTab] = useState('overtime');
  // Aug 2026 — per-pending-row approved-duration inputs (defaults to requested).
  // Shape: { [reqId]: { hours: n, minutes: n } }
  const [approvedDur, setApprovedDur] = useState({});
  // Super-admin edit-any dialog.
  const [editing, setEditing] = useState(null); // { row, date, hours, minutes, approvedHours, approvedMinutes, reason }
  const [editSaving, setEditSaving] = useState(false);

  const getApprovedFor = (r) => {
    if (approvedDur[r.id]) return approvedDur[r.id];
    const mins = Number(r.requested_minutes ?? r.duration_minutes ?? 0);
    return { hours: Math.floor(mins / 60), minutes: mins % 60 };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/work-tracking/report`, {
        params: { date_from: dateFrom, date_to: dateTo, admin_id: adminId || undefined },
        headers: authHeaders,
      });
      setReport(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load work tracking report';
      toast.error(msg);
      setReport(null);
    } finally { setLoading(false); }
  }, [authHeaders, dateFrom, dateTo, adminId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (reqId, action) => {
    setBusyId(reqId);
    try {
      const body = { note: (reviewNote[reqId] || '').trim() };
      if (action === 'approve') {
        const dur = approvedDur[reqId];
        if (dur) {
          const total = Number(dur.hours || 0) * 60 + Number(dur.minutes || 0);
          if (total > 16 * 60) {
            toast.error('Approved duration cannot exceed 16 hours.');
            setBusyId('');
            return;
          }
          body.approved_minutes = total;
        }
      }
      await axios.post(`${API}/admin/overtime-requests/${reqId}/${action}`, body, { headers: authHeaders });
      toast.success(`Request ${action}ed. User notified by email.`);
      setReviewNote((n) => ({ ...n, [reqId]: '' }));
      setApprovedDur((n) => { const c = { ...n }; delete c[reqId]; return c; });
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || `Failed to ${action}`;
      toast.error(msg);
    } finally { setBusyId(''); }
  };

  const openEdit = (r) => {
    const reqMins = Number(r.requested_minutes ?? r.duration_minutes ?? 0);
    const apprMins = r.approved_minutes != null ? Number(r.approved_minutes) : reqMins;
    setEditing({
      row: r,
      date: r.date,
      hours: Math.floor(reqMins / 60),
      minutes: reqMins % 60,
      approvedHours: Math.floor(apprMins / 60),
      approvedMinutes: apprMins % 60,
      reason: r.reason || '',
      status: r.status || 'pending',
      reviewNote: r.review_note || '',
      // Feb 2026 — HH:MM window.
      start_time: r.start_time || '18:00',
      end_time: r.end_time || '20:00',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const total = Number(editing.hours) * 60 + Number(editing.minutes);
    const apprTotal = Number(editing.approvedHours) * 60 + Number(editing.approvedMinutes);
    if (total < 1) { toast.error('Requested duration must be greater than zero.'); return; }
    if (total > 16 * 60) { toast.error('Requested duration cannot exceed 16 hours.'); return; }
    if (apprTotal > 16 * 60) { toast.error('Approved duration cannot exceed 16 hours.'); return; }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(editing.start_time || '') || !/^([01]\d|2[0-3]):[0-5]\d$/.test(editing.end_time || '')) {
      toast.error('Enter both start and end times as HH:MM.'); return;
    }
    if ((editing.start_time || '') >= (editing.end_time || '')) {
      toast.error('End time must be after start time.'); return;
    }
    setEditSaving(true);
    try {
      const body = {
        date: editing.date,
        duration_minutes: total,
        reason: (editing.reason || '').trim(),
        status: editing.status || editing.row.status,
        review_note: (editing.reviewNote ?? editing.row.review_note ?? '').trim(),
        start_time: editing.start_time,
        end_time: editing.end_time,
      };
      // Approved-minutes only meaningful when the row is (or is being
      // flipped to) approved. Backend clears it otherwise so we send
      // it unconditionally for the approved branch.
      if ((editing.status || editing.row.status) === 'approved') {
        body.approved_minutes = apprTotal;
      }
      await axios.put(`${API}/admin/overtime-requests/${editing.row.id}/admin-edit`, body, { headers: authHeaders });
      toast.success('Request updated.');
      setEditing(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update request';
      toast.error(msg);
    } finally { setEditSaving(false); }
  };

  // Aug 2026 — Super-admin overrides (delete overtime, edit + delete
  // desk-activity). All gated to `isSuperAdmin` in the UI + endpoint.
  const [deskEditing, setDeskEditing] = useState(null); // populated desk row
  const [deskEditSaving, setDeskEditSaving] = useState(false);

  const deleteOvertime = async (row) => {
    if (!window.confirm(`Delete overtime request for ${row.admin_name || row.admin_email} on ${row.date}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/overtime-requests/${row.id}`, { headers: authHeaders });
      toast.success('Overtime request deleted.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const openDeskEdit = (row) => {
    const toLocalInput = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setDeskEditing({
      row,
      category: row.category || 'other',
      reason: row.reason || '',
      outAt: toLocalInput(row.out_at),
      inAt: toLocalInput(row.in_at),
      outcome: row.outcome || '',
      failureReason: row.failure_reason || '',
    });
  };

  const saveDeskEdit = async () => {
    if (!deskEditing) return;
    if (!deskEditing.outAt) { toast.error('Out timestamp is required.'); return; }
    if (deskEditing.outcome === 'unsuccess' && !deskEditing.failureReason.trim()) {
      toast.error('Failure reason is required when outcome is Unsuccess.');
      return;
    }
    setDeskEditSaving(true);
    try {
      const body = {
        category: deskEditing.category,
        reason: (deskEditing.reason || '').trim(),
        out_at: new Date(deskEditing.outAt).toISOString(),
        in_at: deskEditing.inAt ? new Date(deskEditing.inAt).toISOString() : null,
        outcome: deskEditing.outcome || '',
        failure_reason: (deskEditing.failureReason || '').trim(),
      };
      await axios.put(`${API}/admin/desk-activity/${deskEditing.row.id}/admin-edit`, body, { headers: authHeaders });
      toast.success('Desk row updated.');
      setDeskEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update desk row');
    } finally { setDeskEditSaving(false); }
  };

  const deleteDeskRow = async (row) => {
    if (!window.confirm(`Delete desk-activity row for ${row.admin_name || row.admin_email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/desk-activity/${row.id}`, { headers: authHeaders });
      toast.success('Desk row deleted.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  // Email → display-name lookup (from the report's admin roster) so
  // the UI can show human names instead of raw email addresses.
  const nameByEmail = useMemo(() => {
    const map = {};
    for (const a of (report?.admins || [])) {
      const em = typeof a?.email === 'string' ? a.email.toLowerCase() : '';
      if (em) map[em] = a.name || a.email;
    }
    return map;
  }, [report]);
  const displayName = (email, fallback) => {
    if (!email || typeof email !== 'string') return fallback || '—';
    const lower = email.toLowerCase();
    return nameByEmail[lower] || fallback || (email.includes('@') ? email.split('@')[0] : email);
  };

  const pending = useMemo(
    () => (report?.overtime_rows || []).filter((r) => r.status === 'pending'),
    [report],
  );
  const history = useMemo(
    () => (report?.overtime_rows || []).filter((r) => r.status !== 'pending'),
    [report],
  );

  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 py-6 space-y-4 max-w-7xl" data-testid="work-tracking-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Overtime & Desk Tracking</h1>
          <p className="text-sm text-slate-500 mt-1">
            Approve overtime claims and audit desk activity across every admin.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="h-9 inline-flex items-center gap-2 px-3 rounded-lg border border-slate-300 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
          data-testid="wt-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <Filter className="w-4 h-4 text-slate-500" />
        <label>
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 h-9 px-3 rounded-lg border border-slate-300 text-sm"
            data-testid="wt-date-from"
          />
        </label>
        <label>
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 h-9 px-3 rounded-lg border border-slate-300 text-sm"
            data-testid="wt-date-to"
          />
        </label>
        <label>
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Admin</span>
          <select
            value={adminId}
            onChange={(e) => setAdminId(e.target.value)}
            className="mt-1 h-9 px-3 rounded-lg border border-slate-300 text-sm min-w-[220px]"
            data-testid="wt-admin-select"
          >
            <option value="">All admins</option>
            {(report?.admins || []).map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.email}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Roll-up cards */}
      {report?.rollup?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="wt-rollup">
          {report.rollup.map((r) => (
            <div key={r.admin_email} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="font-bold text-slate-900 truncate">{displayName(r.admin_email, r.admin_name)}</div>
              <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
                <div>OT approved: <b className="text-emerald-700">{r.overtime_approved_display}</b></div>
                <div>OT pending: <b className="text-amber-700">{r.overtime_pending}</b></div>
                <div>Desk breaks: <b>{r.desk_breaks}</b></div>
                <div>Desk time: <b>{r.desk_minutes_display}</b></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab switcher */}
      <div className="inline-flex rounded-lg bg-slate-100 p-1 text-xs font-bold">
        {['overtime', 'desk'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-md ${tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            data-testid={`wt-tab-${t}`}
          >
            {t === 'overtime' ? 'Overtime requests' : 'Desk activity'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
        </div>
      ) : tab === 'overtime' ? (
        <>
          {/* Pending queue */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="wt-pending">
            <div className="px-4 py-3 border-b border-amber-200 text-sm font-bold text-amber-900">
              Awaiting review · {pending.length}
            </div>
            {pending.length === 0 ? (
              <div className="px-4 py-5 text-sm text-slate-500 italic">Nothing to approve right now.</div>
            ) : (
              <div className="divide-y divide-amber-100">
                {pending.map((r) => {
                  const dur = getApprovedFor(r);
                  return (
                    <div key={r.id} className="px-4 py-3 grid grid-cols-1 lg:grid-cols-4 gap-3" data-testid={`wt-request-${r.id}`}>
                      <div>
                        <div className="font-bold text-slate-900">{displayName(r.admin_email, r.admin_name)}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          <span className="font-mono">{r.date}</span>
                          {r.time_window_display ? <> · <span className="font-mono text-slate-700">{r.time_window_display}</span></> : null}
                          {' '}· Submitted {r.created_at_display}
                          <span className="ml-2"><OvertimeSlaChip row={r} dataTestPrefix="wt-pending-sla" /></span>
                        </div>
                      </div>
                      <div className="lg:col-span-2 text-sm">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Requested</div>
                        <div className="font-bold">{r.requested_display || r.duration_display}</div>
                        <div className="text-slate-600 mt-2 whitespace-pre-wrap">{r.reason || <em className="text-slate-400">No reason provided.</em>}</div>
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Approved duration</div>
                          <div className="grid grid-cols-2 gap-2 max-w-[220px]">
                            <label className="block">
                              <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">Hours</span>
                              <input
                                type="number" min="0" max="16" value={dur.hours}
                                onChange={(e) => setApprovedDur((n) => ({ ...n, [r.id]: { ...n[r.id], hours: Math.max(0, Math.min(16, parseInt(e.target.value || '0', 10))) } }))}
                                className="h-9 w-full rounded-lg border border-slate-300 px-3 text-sm text-center"
                                data-testid={`wt-approve-hours-${r.id}`}
                              />
                            </label>
                            <label className="block">
                              <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">Minutes</span>
                              <input
                                type="number" min="0" max="59" value={dur.minutes}
                                onChange={(e) => setApprovedDur((n) => ({ ...n, [r.id]: { ...n[r.id], minutes: Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10))) } }))}
                                className="h-9 w-full rounded-lg border border-slate-300 px-3 text-sm text-center"
                                data-testid={`wt-approve-minutes-${r.id}`}
                              />
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={reviewNote[r.id] || ''}
                          onChange={(e) => setReviewNote((n) => ({ ...n, [r.id]: e.target.value }))}
                          placeholder="Reviewer note (optional)"
                          className="h-9 w-full rounded-lg border border-slate-300 px-3 text-xs focus:ring-2 focus:ring-sky-500 focus:outline-none"
                          data-testid={`wt-note-${r.id}`}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => decide(r.id, 'approve')}
                            disabled={busyId === r.id}
                            className="flex-1 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center justify-center gap-1 disabled:opacity-60"
                            data-testid={`wt-approve-${r.id}`}
                          >
                            {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => decide(r.id, 'reject')}
                            disabled={busyId === r.id}
                            className="flex-1 h-9 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold inline-flex items-center justify-center gap-1 disabled:opacity-60"
                            data-testid={`wt-reject-${r.id}`}
                          >
                            <XCircle className="w-4 h-4" /> Reject
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="w-full h-8 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-[11px] font-semibold inline-flex items-center justify-center gap-1"
                          data-testid={`wt-edit-${r.id}`}
                        >
                          <Pencil className="w-3 h-3" /> Edit request
                        </button>
                        {isSuperAdmin && (
                          <button
                            type="button"
                            onClick={() => deleteOvertime(r)}
                            className="w-full h-8 rounded-lg border border-red-200 hover:bg-red-50 text-red-700 text-[11px] font-semibold inline-flex items-center justify-center gap-1"
                            data-testid={`wt-delete-${r.id}`}
                            title="Super-admin delete"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* History */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
              <div className="text-sm font-bold text-slate-900">
                History · {history.length}
              </div>
              <div className="flex items-center gap-1.5">
                <a
                  href={`${API}/admin/overtime-requests/export.csv?scope=approved&from_date=${encodeURIComponent(dateFrom)}&to_date=${encodeURIComponent(dateTo)}&_t=${authHeaders.Authorization ? '' : ''}`}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const res = await axios.get(
                        `${API}/admin/overtime-requests/export.csv`,
                        {
                          params: { scope: 'approved', from_date: dateFrom, to_date: dateTo },
                          headers: authHeaders,
                          responseType: 'blob',
                        },
                      );
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `overtime-approved-${dateFrom}-${dateTo}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'CSV export failed');
                    }
                  }}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-xs font-bold text-slate-700"
                  data-testid="wt-history-export-approved"
                  title="Approved slots only — one row per approved overtime, HR-payroll ready"
                >
                  <Download className="w-3.5 h-3.5" /> Download CSV
                </a>
                {/* Feb 2026 — Branded PDF of the filtered overtime
                    requests table (User, Date, Requested/Approved
                    duration, Status, Reason). */}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const params = { date_from: dateFrom, date_to: dateTo };
                      if (adminId) params.admin_id = adminId;
                      const res = await axios.get(
                        `${API}/admin/work-tracking/overtime/pdf`,
                        { params, headers: authHeaders, responseType: 'blob' },
                      );
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `overtime-requests-${dateFrom}-${dateTo}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'PDF export failed');
                    }
                  }}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-rose-300 hover:bg-rose-50 text-xs font-bold text-rose-700"
                  data-testid="wt-history-export-pdf"
                  title="Download the filtered overtime requests as a branded PDF report"
                >
                  <FileText className="w-3.5 h-3.5" /> Export PDF
                </button>
              </div>
            </div>
            {history.length === 0 ? (
              <div className="px-4 py-5 text-sm text-slate-500 italic">No approved / rejected requests in this range.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Admin</th>
                      <th className="text-left px-4 py-2">Date</th>
                      <th className="text-left px-4 py-2">Start</th>
                      <th className="text-left px-4 py-2">End</th>
                      <th className="text-left px-4 py-2">Total</th>
                      <th className="text-left px-4 py-2">Approved</th>
                      <th className="text-center px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Reviewer</th>
                      <th className="text-left px-4 py-2">Note</th>
                      <th className="text-right px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map((r) => (
                      <tr key={r.id} data-testid={`wt-history-${r.id}`}>
                        <td className="px-4 py-2">
                          <div className="font-semibold">{displayName(r.admin_email, r.admin_name)}</div>
                        </td>
                        <td className="px-4 py-2 font-mono">{r.date}</td>
                        <td className="px-4 py-2 font-mono text-slate-700" data-testid={`wt-history-start-${r.id}`}>{r.start_time || '—'}</td>
                        <td className="px-4 py-2 font-mono text-slate-700" data-testid={`wt-history-end-${r.id}`}>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span>{r.end_time || '—'}</span>
                            <OvertimeSlaChip row={r} dataTestPrefix="wt-history-sla" />
                          </div>
                        </td>
                        <td className="px-4 py-2 font-semibold" data-testid={`wt-history-total-${r.id}`}>{r.requested_display || r.duration_display}</td>
                        <td className="px-4 py-2 font-semibold text-emerald-700">{r.approved_display || '—'}</td>
                        <td className="px-4 py-2 text-center"><StatusChip status={r.status} /></td>
                        <td className="px-4 py-2 text-slate-600 text-[11px]">
                          <div>{displayName(r.reviewed_by)}</div>
                          <div className="text-slate-400">{r.reviewed_at_display}</div>
                        </td>
                        <td className="px-4 py-2 text-slate-600 max-w-[260px] truncate" title={r.review_note}>{r.review_note || '—'}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEdit(r)}
                              className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold text-slate-700"
                              data-testid={`wt-history-edit-${r.id}`}
                              title="Edit this request"
                            >
                              <Pencil className="w-3 h-3" /> Edit
                            </button>
                            {isSuperAdmin && (
                              <button
                                type="button"
                                onClick={() => deleteOvertime(r)}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-200 hover:bg-red-50 text-[11px] font-semibold text-red-700"
                                data-testid={`wt-history-delete-${r.id}`}
                                title="Super-admin delete"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Super-admin edit-any dialog */}
          {editing && (
            <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-testid="wt-edit-dialog">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <div className="text-sm font-bold text-slate-900">
                    Edit overtime request
                    <span className="ml-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{editing.row.status}</span>
                  </div>
                  <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600" data-testid="wt-edit-close">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div className="text-[11px] text-slate-500">
                    <span className="font-semibold">Requester:</span> {displayName(editing.row.admin_email, editing.row.admin_name)}
                  </div>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Date</span>
                    <input
                      type="date"
                      value={editing.date}
                      onChange={(e) => setEditing((v) => ({ ...v, date: e.target.value }))}
                      className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                      data-testid="wt-edit-date"
                    />
                  </label>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Requested duration</span>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">Hours</span>
                        <input
                          type="number" min="0" max="16" value={editing.hours}
                          onChange={(e) => setEditing((v) => ({ ...v, hours: Math.max(0, Math.min(16, parseInt(e.target.value || '0', 10))) }))}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-center"
                          data-testid="wt-edit-req-hours"
                        />
                      </label>
                      <label className="block">
                        <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">Minutes</span>
                        <input
                          type="number" min="0" max="59" value={editing.minutes}
                          onChange={(e) => setEditing((v) => ({ ...v, minutes: Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10))) }))}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-center"
                          data-testid="wt-edit-req-minutes"
                        />
                      </label>
                    </div>
                  </div>
                  {(editing.status || editing.row.status) === 'approved' && (
                    <div>
                      <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Approved duration</span>
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        <label className="block">
                          <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">Hours</span>
                          <input
                            type="number" min="0" max="16" value={editing.approvedHours}
                            onChange={(e) => setEditing((v) => ({ ...v, approvedHours: Math.max(0, Math.min(16, parseInt(e.target.value || '0', 10))) }))}
                            className="h-10 w-full rounded-lg border border-emerald-300 px-3 text-sm text-center"
                            data-testid="wt-edit-appr-hours"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">Minutes</span>
                          <input
                            type="number" min="0" max="59" value={editing.approvedMinutes}
                            onChange={(e) => setEditing((v) => ({ ...v, approvedMinutes: Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10))) }))}
                            className="h-10 w-full rounded-lg border border-emerald-300 px-3 text-sm text-center"
                            data-testid="wt-edit-appr-minutes"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Time window (Muscat)</span>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">Start</span>
                        <input
                          type="time"
                          value={editing.start_time || ''}
                          onChange={(e) => setEditing((v) => ({ ...v, start_time: e.target.value }))}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          data-testid="wt-edit-start"
                        />
                      </label>
                      <label className="block">
                        <span className="text-[10px] font-semibold text-slate-500 block mb-0.5">End</span>
                        <input
                          type="time"
                          value={editing.end_time || ''}
                          onChange={(e) => setEditing((v) => ({ ...v, end_time: e.target.value }))}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          data-testid="wt-edit-end"
                        />
                      </label>
                    </div>
                  </div>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Reason</span>
                    <textarea
                      value={editing.reason}
                      onChange={(e) => setEditing((v) => ({ ...v, reason: e.target.value }))}
                      className="mt-1 w-full min-h-[72px] rounded-lg border border-slate-300 p-3 text-sm"
                      data-testid="wt-edit-reason"
                    />
                  </label>
                  {isSuperAdmin && (
                    <>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-fuchsia-700">Status (super-admin override)</span>
                        <select
                          value={editing.status}
                          onChange={(e) => setEditing((v) => ({ ...v, status: e.target.value }))}
                          className="mt-1 w-full h-10 rounded-lg border border-fuchsia-300 px-3 text-sm font-semibold"
                          data-testid="wt-edit-status"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Reviewer note</span>
                        <input
                          type="text"
                          value={editing.reviewNote || ''}
                          onChange={(e) => setEditing((v) => ({ ...v, reviewNote: e.target.value }))}
                          className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                          placeholder="Note added by the reviewer"
                          data-testid="wt-edit-review-note"
                        />
                      </label>
                    </>
                  )}
                </div>
                <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
                  <button type="button" onClick={() => setEditing(null)} className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={editSaving}
                    className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm font-bold flex items-center gap-1 disabled:opacity-60"
                    data-testid="wt-edit-save"
                  >
                    {editSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
            <div className="text-sm font-bold text-slate-900 flex-1">
              Desk activity · {(report?.desk_rows || []).length}
            </div>
            {/* Feb 2026 — Branded PDF of the filtered Desk Activity
                table (User, Out/In, Duration, Category, Reason). */}
            <button
              type="button"
              onClick={async () => {
                try {
                  const params = { date_from: dateFrom, date_to: dateTo };
                  if (adminId) params.admin_id = adminId;
                  const res = await axios.get(
                    `${API}/admin/work-tracking/desk-activity/pdf`,
                    { params, headers: authHeaders, responseType: 'blob' },
                  );
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `desk-activity-${dateFrom}-${dateTo}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'PDF export failed');
                }
              }}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-rose-300 hover:bg-rose-50 text-xs font-bold text-rose-700"
              data-testid="wt-desk-export-pdf"
              title="Download the filtered desk activity as a branded PDF report"
            >
              <FileText className="w-3.5 h-3.5" /> Export PDF
            </button>
          </div>
          {(report?.desk_rows || []).length === 0 ? (
            <div className="px-4 py-5 text-sm text-slate-500 italic">No desk activity in this range.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Admin</th>
                    <th className="text-left px-4 py-2">Out</th>
                    <th className="text-left px-4 py-2">In</th>
                    <th className="text-left px-4 py-2">Duration</th>
                    <th className="text-left px-4 py-2">Category</th>
                    <th className="text-left px-4 py-2">Leave reason</th>
                    <th className="text-left px-4 py-2">Outcome</th>
                    <th className="text-left px-4 py-2">Failure reason</th>
                    {isSuperAdmin && <th className="text-right px-4 py-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(report?.desk_rows || []).map((r) => (
                    <tr key={r.id} data-testid={`wt-desk-row-${r.id}`}>
                      <td className="px-4 py-2">
                        <div className="font-semibold">{displayName(r.admin_email, r.admin_name)}</div>
                      </td>
                      <td className="px-4 py-2 tabular-nums">{r.out_at_display || '—'}</td>
                      <td className="px-4 py-2 tabular-nums">{r.in_at_display || <span className="text-amber-700 font-semibold">Open</span>}</td>
                      <td className="px-4 py-2 font-semibold">{r.duration_display || '—'}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                          {r.category_label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-600 max-w-[220px] truncate" title={r.reason}>{r.reason || '—'}</td>
                      <td className="px-4 py-2">
                        {r.outcome === 'success' && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800" data-testid={`wt-desk-outcome-${r.id}`}>Success</span>
                        )}
                        {r.outcome === 'unsuccess' && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800" data-testid={`wt-desk-outcome-${r.id}`}>Unsuccess</span>
                        )}
                        {!r.outcome && <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-slate-600 max-w-[260px] truncate" title={r.failure_reason}>{r.failure_reason || '—'}</td>
                      {isSuperAdmin && (
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openDeskEdit(r)}
                              className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold text-slate-700"
                              data-testid={`wt-desk-edit-${r.id}`}
                              title="Super-admin edit"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteDeskRow(r)}
                              className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-200 hover:bg-red-50 text-[11px] font-semibold text-red-700"
                              data-testid={`wt-desk-delete-${r.id}`}
                              title="Super-admin delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Super-admin desk-activity edit dialog */}
      {deskEditing && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-testid="wt-desk-edit-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="text-sm font-bold text-slate-900">
                Edit desk-activity row
                <span className="ml-2 text-[10px] font-semibold text-fuchsia-600 uppercase tracking-wider">SUPER-ADMIN</span>
              </div>
              <button type="button" onClick={() => setDeskEditing(null)} className="text-slate-400 hover:text-slate-600" data-testid="wt-desk-edit-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              <div className="text-[11px] text-slate-500">
                <span className="font-semibold">User:</span> {displayName(deskEditing.row.admin_email, deskEditing.row.admin_name)}
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Category</span>
                <select
                  value={deskEditing.category}
                  onChange={(e) => setDeskEditing((v) => ({ ...v, category: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                  data-testid="wt-desk-edit-category"
                >
                  <option value="prayer">Prayer</option>
                  <option value="lunch">Lunch</option>
                  <option value="meeting">Meeting</option>
                  <option value="personal">Personal</option>
                  <option value="cash_deposit">Cash Deposit</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Out (local)</span>
                  <input
                    type="datetime-local"
                    value={deskEditing.outAt}
                    onChange={(e) => setDeskEditing((v) => ({ ...v, outAt: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                    data-testid="wt-desk-edit-out"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">In (local, blank = open)</span>
                  <input
                    type="datetime-local"
                    value={deskEditing.inAt}
                    onChange={(e) => setDeskEditing((v) => ({ ...v, inAt: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                    data-testid="wt-desk-edit-in"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Leave reason</span>
                <textarea
                  value={deskEditing.reason}
                  onChange={(e) => setDeskEditing((v) => ({ ...v, reason: e.target.value }))}
                  className="mt-1 w-full min-h-[64px] rounded-lg border border-slate-300 p-3 text-sm"
                  data-testid="wt-desk-edit-reason"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Outcome</span>
                <select
                  value={deskEditing.outcome}
                  onChange={(e) => setDeskEditing((v) => ({ ...v, outcome: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                  data-testid="wt-desk-edit-outcome"
                >
                  <option value="">— (still open)</option>
                  <option value="success">Success</option>
                  <option value="unsuccess">Unsuccess</option>
                </select>
              </label>
              {deskEditing.outcome === 'unsuccess' && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-red-700">Failure reason (required)</span>
                  <textarea
                    value={deskEditing.failureReason}
                    onChange={(e) => setDeskEditing((v) => ({ ...v, failureReason: e.target.value }))}
                    className="mt-1 w-full min-h-[64px] rounded-lg border border-red-300 p-3 text-sm"
                    data-testid="wt-desk-edit-failure-reason"
                  />
                </label>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button type="button" onClick={() => setDeskEditing(null)} className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveDeskEdit}
                disabled={deskEditSaving}
                className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm font-bold flex items-center gap-1 disabled:opacity-60"
                data-testid="wt-desk-edit-save"
              >
                {deskEditSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminWorkTracking;
